import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";

import { getDispensaryPhotos } from "@/api/requests/getDispensaryPhotos";
import { DispensaryPhoto } from "@/custom-types/Dispensary";

const LightBox = dynamic(() => import("@/components/LightBox"));

interface DispensaryPhotosLightBoxProps {
  initialPhotos?: DispensaryPhoto[];
  initialIndex?: number;
  dispensarySlug: string;
  onClose: () => void;
  showLightbox?: boolean;
}

/**
 * This component wraps the {@link LightBox} component specifically to render dispensary
 * photos. Additionally, this component supports pagination, fetching new images from the
 * API as needed.
 */
const DispensaryPhotosLightBox = ({
  showLightbox = false,
  initialPhotos,
  initialIndex,
  dispensarySlug,
  onClose,
}: DispensaryPhotosLightBoxProps) => {
  const calculateInitialSkip = (
    photos: DispensaryPhoto[],
    takeCount: number,
  ) => {
    if (photos.length >= takeCount) {
      // Exclude photos w/ ID = -1 in the calculation. Any photo with a -1 ID
      // is not a standard photo we would get from the dispensary photos endpoint.
      // For example, we artifically add the dispensary cover photo in order to
      // make it available to view in the LightBox.
      return photos.filter((photo) => photo.id !== -1).length - takeCount;
    }

    return 0;
  };

  const [photos, setPhotos] = useState<DispensaryPhoto[]>(initialPhotos || []);
  const take = 10;
  const skip = useRef(calculateInitialSkip(photos, take));
  const reachedEnd = useRef(false);

  const loadPhotos = async (skip?: number) => {
    const response = await getDispensaryPhotos(dispensarySlug, take, skip || 0);
    if (response.data.length > 0) {
      const newPhotos = [...photos, ...response.data];
      reachedEnd.current = newPhotos.length >= response.metadata.totalCount;
      setPhotos(newPhotos);
    }
  };

  useEffect(() => {
    if (photos.length === 0) {
      loadPhotos();
    }
  }, []);

  const onNext = (index: number) => {
    // Paginate when user has reached last image.
    const shouldLoadMore = !reachedEnd.current && index === photos.length - 1;
    if (shouldLoadMore) {
      skip.current = skip.current + take;
      loadPhotos(skip.current);
    }
  };

  return (
    <LightBox
      images={photos}
      initialPhotoIndex={initialIndex}
      onToggleLightboxClick={onClose}
      onMoveNextClick={onNext}
      disableReverseLoop={true}
      open={showLightbox}
    />
  );
};

export default DispensaryPhotosLightBox;
